import { useEffect, useState } from 'react';

import { StoreSchemaKey } from '@/app/providers/store';
import { useAppDispatch } from '@/shared/lib/hooks/use-app-dispatch.hook';
import { useAppStore } from '@/shared/lib/hooks/use-app-store.hook';

import { LazyReducerProps } from './lazy-reducer.types';

/**
 * Component for lazy reducer
 * @param {ReactNode} [children] - The component around which it is formed
 * @param {ReducerList} [reducers] - A list of reducers
 * @param {Boolean} [hasUnmount=true] - The value determines whether the component will be unmounted
 * @returns null | Element
 * @example
 * const reducers: ReducerList = {
 * 	 reducer: exampleReducer,
 * };
 *
 * <LazyReducer reducers={reducers}>
 *   <Box>
 *     ...
 *   </Box>
 * </LazyReducer>
 */

const LazyReducerComponent = ({ children, reducers, hasUnmount = true }: LazyReducerProps) => {
	const [mounted, setMounted] = useState(false);
	const { reducerManager } = useAppStore();
	const dispatch = useAppDispatch();

	useEffect(() => {
		Object.entries(reducers).forEach(([key, reducer]) => {
			reducerManager.add(key as StoreSchemaKey, reducer);
			dispatch({ type: `@INIT ${key} reducer` });
		});
		setMounted(true);

		return () => {
			if (hasUnmount) {
				Object.entries(reducers).forEach(([key]) => {
					reducerManager.remove(key as StoreSchemaKey);
					dispatch({ type: `@DESTROY ${key} reducer` });
				});
			}
		};
	}, [dispatch, reducerManager, reducers, hasUnmount]);

	if (!mounted) {
		return null;
	}

	return <>{children}</>;
};

export default LazyReducerComponent;
